import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import Loader from '../Common/Loader';
import { getStudents, assignCourseToStudent, removeStudent, getCourses, inviteStudent, setStudentZoomLink, setStudentActive, deleteStudentForever, ensureStudentCode , updateStudent } from '../../services/adminService';
import Modal from '../Common/Modal';
import StudentProfileModal from './StudentProfileModal';
import { CoverPattern, shade } from './CourseCover';
import Dropdown from '../Common/Dropdown';
import { confirmDialog } from '../Common/confirm';
import { avatarColor, AvatarIcon } from '../../utils/avatar';
import toast from 'react-hot-toast';
import { CourseIcon as LibIcon, getIcon } from './iconLibrary';

/* Строка ученика: выбор · ученик · код · курс · регистрация · активность · шеврон.
   Сама сетка живёт в Admin.css (.stu-row / .stu-head) — на узких экранах медиазапросы
   прячут менее важные колонки, поэтому горизонтальной прокрутки нет. */

const STU_HEAD = { fontSize: 10.5, fontWeight: 800, letterSpacing: '0.6px', textTransform: 'uppercase', color: 'var(--text-3)' };

// Пустой квадратик выбора (пустой → выбран галочкой)
const Check = ({ on }) => (
  <span style={{ width: 18, height: 18, borderRadius: 5, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', transition: '.12s', border: `1.8px solid ${on ? 'var(--primary)' : 'var(--border)'}`, background: on ? 'var(--primary)' : 'transparent' }}>
    {on && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>}
  </span>
);

/* ── Иконки ─────────────────────────────────────────────────────────────── */
const IcoBook = ({ s = 15 }) => (
  <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
  </svg>
);
const IcoCap = ({ s = 15 }) => (
  <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c0 2 2 3 6 3s6-1 6-3v-5"/>
  </svg>
);
const IcoLayers = ({ s = 15 }) => (
  <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/>
  </svg>
);
// Иконка курса: сначала та, что админ выбрал курсу (она же на обложке),
// иначе по типу экзамена — книга (ОГЭ), шапочка (ЕГЭ), слои (прочее)
const CourseIcon = ({ icon, name = '', examType, size = 15, sw = 1.8 }) => {
  if (icon) {
    const lib = <LibIcon icon={icon} size={size} sw={sw} />;
    if (getIcon(icon)) return lib;
  }
  const t = examType || name;
  if (/ОГЭ/i.test(t)) return <IcoBook s={size} />;
  if (/ЕГЭ/i.test(t)) return <IcoCap s={size} />;
  return <IcoLayers s={size} />;
};

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);
const IcRows = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <line x1="3" y1="7" x2="21" y2="7"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="17" x2="21" y2="17"/>
  </svg>
);
const IcCards = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7.5" height="7.5" rx="1.6"/><rect x="13.5" y="3" width="7.5" height="7.5" rx="1.6"/>
    <rect x="3" y="13.5" width="7.5" height="7.5" rx="1.6"/><rect x="13.5" y="13.5" width="7.5" height="7.5" rx="1.6"/>
  </svg>
);
const IcArchive = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="20" height="5" rx="1.6"/><path d="M4 9v9a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9"/><line x1="10" y1="14" x2="14" y2="14"/>
  </svg>
);
const IcBack = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
  </svg>
);
const IcChevron = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-3)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);

/* ── Форматирование ─────────────────────────────────────────────────────── */
const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

/* «Был(а) онлайн»: дата последнего визита, а если ученик ни разу не заходил —
   дата регистрации. Цветом статус больше не размечаем: точка у всех зелёная. */
const onlineLabel = (student) => formatDate(student.lastActiveAt || student.created_at);
const OnlineCell = ({ student, size = 13.5 }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
    <span style={{ width: 7, height: 7, borderRadius: '50%', flexShrink: 0, background: 'var(--stu-green)' }} />
    <span style={{ fontSize: size, fontWeight: 600, color: 'var(--text-1)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
      {onlineLabel(student)}
    </span>
  </span>
);

/* «Пройдено» — мини-кольцо прогресса + процент шрифтом Bebas.
   Кольцо на svg: conic-gradient потребовал бы непрозрачную «дырку»,
   а фон строки полупрозрачный и меняется на hover. */
function DoneCell({ student }) {
  // Процент — с сервера, той же формулой, что «Общий прогресс» в карточке ученика:
  // пройдено из всех занятий курса (вебинары + ДЗ + варианты)
  const st = student.progressStats || {};
  const total = st.totalLessons || 0;
  const pct = st.progress || 0;
  const R = 7.5, C = 2 * Math.PI * R;
  const color = pct >= 100 ? 'var(--stu-green)' : pct > 0 ? 'var(--primary)' : 'var(--text-3)';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
      title={total ? `${st.completedLessons || 0} из ${total} уроков` : 'нет уроков'}>
      <svg width="19" height="19" viewBox="0 0 19 19" style={{ flexShrink: 0, transform: 'rotate(-90deg)' }}>
        <circle cx="9.5" cy="9.5" r={R} fill="none" stroke="var(--border)" strokeWidth="2.6" />
        {pct > 0 && (
          <circle cx="9.5" cy="9.5" r={R} fill="none" stroke={color} strokeWidth="2.6" strokeLinecap="round"
            strokeDasharray={`${(C * pct) / 100} ${C}`} />
        )}
      </svg>
      {/* кегль и подъём — как у названия курса рядом, чтобы строки читались одной линией */}
      <span className={`stu-done-num${pct > 0 ? '' : ' zero'}`}>{pct}<i>%</i></span>
    </span>
  );
}

/* Пара стрелок (вверх/вниз) у сортируемого заголовка; активная подсвечена */
const Tri = ({ dir, active }) => (
  <svg width="8" height="5" viewBox="0 0 8 5" style={{ display: 'block' }}>
    <path d={dir === 'up' ? 'M4 0l4 5H0z' : 'M4 5L0 0h8z'}
      fill={active ? 'var(--primary)' : 'var(--text-3)'} opacity={active ? 1 : 0.4} />
  </svg>
);

function SortHead({ label, col, sortBy, sortDir, onSort, align }) {
  const active = sortBy === col;
  return (
    <span
      onClick={() => onSort(col)}
      title="Сортировать"
      style={{ ...STU_HEAD, display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
        userSelect: 'none', color: active ? 'var(--primary-dark)' : 'var(--text-3)',
        justifyContent: align === 'center' ? 'center' : 'flex-start' }}
    >
      {label}
      <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
        <Tri dir="up"   active={active && sortDir === 'asc'} />
        <Tri dir="down" active={active && sortDir === 'desc'} />
      </span>
    </span>
  );
}

/* Код ученика для родителя: кликабельный моно-чип, копирует код в буфер.
   Если кода ещё нет — по клику просим бэкенд сгенерировать и сразу копируем. */
function CodeChip({ student, onCode, size = 'row' }) {
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);
  const code = student.progressCode || '';

  const copy = async (e) => {
    e.stopPropagation();
    if (busy) return;
    let value = code;
    if (!value) {
      setBusy(true);
      try {
        const res = await ensureStudentCode(student.id);
        value = res?.data?.code || '';
        if (value) onCode?.(student.id, value);
      } catch { toast.error('Не удалось создать код'); setBusy(false); return; }
      setBusy(false);
      if (!value) return;
    }
    try {
      await navigator.clipboard.writeText(value);
      setOk(true); setTimeout(() => setOk(false), 1400);
      toast.success(`Код ${value} скопирован`);
    } catch { toast.error('Буфер обмена недоступен'); }
  };

  const big = size === 'card';
  // Пробному код для родителя не выдаём — в этом поле стоит пометка «Пробный»
  if (student.isTrial) {
    return (
      <span className={`stu-code stu-code-trial${big ? ' big' : ''}`} title="Пробный ученик — код для родителя появится после зачисления">
        Пробный
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={copy}
      title={code ? 'Скопировать код для родителя' : 'Создать код и скопировать'}
      className={`stu-code${big ? ' big' : ''}`}
      data-ok={ok ? 'true' : undefined}
    >
      {busy ? '····' : (code || '····')}
      <svg className="stu-code-ic" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        {ok
          ? <polyline points="20 6 9 17 4 12" />
          : <><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></>}
      </svg>
    </button>
  );
}

/* Курс: иконка в рамке — как значок на обложке курса — и название тем же
   шрифтом, что заголовок обложки (Saar + Bebas). */
function CourseCell({ student, onRemove, compact }) {
  const list = student.StudentCourses || [];
  if (list.length === 0) {
    return (
      <span className="stu-course" style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
        <span className={`stu-crs-ic empty${compact ? ' sm' : ''}`} />
        <span className={`stu-crs-name empty${compact ? ' sm' : ''}`}>Без курса</span>
      </span>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7, minWidth: 0 }}>
      {list.map(sc => {
        const nm = sc.Course?.name || 'Курс';
        return (
          <span key={sc.Course?.id || nm} className="stu-course" style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <span className={`stu-crs-ic${compact ? ' sm' : ''}`}>
              <CourseIcon icon={sc.Course?.icon} name={nm} examType={sc.Course?.examType} size={compact ? 15 : 17} sw={2} />
            </span>
            <span className={`stu-crs-name${compact ? ' sm' : ''}`}>{nm}</span>
            {onRemove && (
              <button
                onClick={(e) => { e.stopPropagation(); onRemove(sc.Course?.id, nm); }}
                title="Убрать с этого курса"
                className="stu-course-x"
                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 16, borderRadius: '50%', border: 'none', background: 'transparent', color: 'var(--text-3)', cursor: 'pointer', padding: 0, flexShrink: 0 }}
              >
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            )}
          </span>
        );
      })}
    </div>
  );
}

const Bar = ({ label, value, pct }) => (
  <div style={{ flex: 1, minWidth: 0 }}>
    <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.5px', textTransform: 'uppercase', color: 'var(--text-3)' }}>{label}</div>
    <div style={{ fontFamily: "'Space Grotesk', var(--font-body)", fontWeight: 600, fontSize: 19, color: 'var(--text-1)', margin: '5px 0 6px', fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    <div style={{ height: 5, borderRadius: 10, background: 'var(--primary-light)', overflow: 'hidden' }}>
      <div style={{ height: 5, borderRadius: 10, background: 'var(--primary)', width: `${pct}%` }} />
    </div>
  </div>
);

// Русское склонение: 1 ученик / 2 ученика / 5 учеников
const plural = (n, f) => { const a = Math.abs(n) % 100, b = a % 10; if (a > 10 && a < 20) return f[2]; if (b > 1 && b < 5) return f[1]; if (b === 1) return f[0]; return f[2]; };

const IcGroupMain = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 20v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9.5" cy="7" r="3.5" /><path d="M22 20v-2a4 4 0 0 0-3-3.87" /><path d="M16.5 3.6a4 4 0 0 1 0 6.8" /></svg>);

const IcCourses = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2 2 7l10 5 10-5-10-5z" /><path d="M2 17l10 5 10-5M2 12l10 5 10-5" /></svg>);
// «Все ученики» в выборе курса — та же иконка, что у раздела «Ученики» в сайдбаре
const IcAllStudents = () => (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>);

export default function StudentsManager() {
  const { can } = useAuth();
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCourse, setFilterCourse] = useState('all');
  /* Приход из «Статистики» по клику на «учеников на курсе»: показываем только
     учеников этого курса. Отбор скрытый — тумблер не двигаем; он слетает,
     как только админ сам трогает поиск, тумблер, сортировку или архив. */
  const [pinnedCourse, setPinnedCourse] = useState(null);
  // Выбор курса вместо тумблера: окно с карточками курсов
  const [coursePick, setCoursePick] = useState(false);
  const [pickedCourse, setPickedCourse] = useState(null);
  /* Группа внутри курса: 'main' | 'trial' | null (все).
     Группа — это и есть пометка «пробник» у ученика: отдельного поля нет,
     чтобы не разъезжались две точки правды (на ней же держатся замки
     на занятиях в пробном доступе). */
  const [pickedGroup, setPickedGroup] = useState(null);
  const [archive, setArchive] = useState(false);   // экран «Архив» (отключённые аккаунты)
  // Полки активных и архива лежат рядом в одном flex-ряду, поэтому «окно» тянулось
  // бы по самой высокой. Держим его высоту равной высоте видимой полки.
  const activePaneRef = useRef(null);
  const archivePaneRef = useRef(null);
  const [swipeH, setSwipeH] = useState(0);
  // Сдвиг и подгонку высоты анимируем только на переходе «ученики ⇄ архив».
  // Иначе смена фильтра (Все/ОГЭ/ЕГЭ) тоже плыла по высоте — список «размазывало».
  const [swiping, setSwiping] = useState(false);
  const [view, setView] = useState(() => localStorage.getItem('adm_students_view') || 'rows');
  const [sortBy, setSortBy]   = useState('date');  // 'name' | 'course' | 'date' | 'active'
  const [sortDir, setSortDir] = useState('desc');  // 'asc' | 'desc' (по умолчанию — по дате, новые сверху)

  const [assignOpen, setAssignOpen]         = useState(false);
  const [inviteOpen, setInviteOpen]         = useState(false);
  const [zoomEditOpen, setZoomEditOpen]     = useState(false);
  const [zoomEditStudent, setZoomEditStudent] = useState(null);
  const [zoomEditValue, setZoomEditValue]   = useState('');
  const [zoomManual, setZoomManual]         = useState(false); // раскрыто поле «вписать вручную»
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedCourse, setSelectedCourse]   = useState('');
  const [inviteFirst, setInviteFirst]         = useState('');
  const [inviteLast, setInviteLast]           = useState('');
  const [inviteTrial, setInviteTrial]         = useState(false);   // пробный ученик
  const [inviteEmail, setInviteEmail]         = useState('');
  const [inviteCourseId, setInviteCourseId]   = useState('');
  const [saving, setSaving]                   = useState(false);
  const [profileId, setProfileId]             = useState(null); // открытый профиль (по id — чтобы пережить перезагрузку списка)
  const [selectedIds, setSelectedIds]         = useState(() => new Set()); // массовый выбор
  const [bulkAssign, setBulkAssign]           = useState(false);
  // Переход из ленты «Последние действия» на Главной: сразу открываем карточку ученика
  const location = useLocation();
  const navigate = useNavigate();

  const setViewMode = (v) => { setView(v); localStorage.setItem('adm_students_view', v); };

  const toggleSelect = (id) => setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const clearSelection = () => setSelectedIds(new Set());
  /* Перенос между группами = снять/поставить пометку «пробник». Отдельного
     поля у зачисления нет специально: на этой пометке держатся замки на
     занятиях в пробном доступе, и две точки правды разъехались бы. */
  const bulkGroup = async (toTrial) => {
    const n = selectedIds.size;
    const title = toTrial ? 'Перевести в пробную группу?' : 'Перевести в основную группу?';
    const message = toTrial
      ? `${n} ученик(ов) получат пометку «пробник»: им останутся только занятия, открытые в «Доступе к пробным». Дата доступа при этом снимается.`
      : `${n} ученик(ов) получат полный доступ к курсу. Дату доступа после этого проставьте в «Доступах».`;
    if (!(await confirmDialog({ title, message, confirmText: 'Перевести' }))) return;
    try {
      // имя обязательно: эта же ручка переименовывает ученика
      await Promise.all([...selectedIds].map(id => {
        const st = students.find(x => x.id === id);
        return updateStudent(id, { name: st?.name || '', isTrial: toTrial }).catch(() => null);
      }));
      toast.success(toTrial ? 'Перенесли в пробную группу' : 'Перенесли в основную группу');
      clearSelection(); loadData();
    } catch { toast.error('Ошибка'); }
  };

  const bulkDeactivate = async () => {
    if (!(await confirmDialog({ title: 'Перенести в архив?', message: `${selectedIds.size} аккаунт(ов) уедут в архив и не смогут войти. Вернуть можно в любой момент.`, confirmText: 'В архив' }))) return;
    try {
      await Promise.all([...selectedIds].map(id => setStudentActive(id, false).catch(() => null)));
      toast.success('Аккаунты отключены');
      clearSelection(); loadData();
    } catch { toast.error('Ошибка'); }
  };

  // В архиве выбранных можно стереть совсем — в активных такой кнопки нет
  const bulkDeleteForever = async () => {
    if (!(await confirmDialog({
      title: 'Удалить навсегда?',
      message: `${selectedIds.size} аккаунт(ов) будут удалены полностью: курсы, прогресс, ответы и история. Восстановить нельзя.`,
      confirmText: 'Удалить навсегда',
    }))) return;
    try {
      await Promise.all([...selectedIds].map(id => deleteStudentForever(id).catch(() => null)));
      toast.success('Ученики удалены');
      clearSelection(); loadData();
    } catch { toast.error('Ошибка'); }
  };

  useEffect(() => { loadData(); }, []);
  useEffect(() => {
    // Приход из «Быстрых действий» в профиле — сразу окно «Добавить ученика»
    if (location.state?.invite) {
      setInviteOpen(true);
      navigate(location.pathname, { replace: true, state: null });
      return;
    }
    if (location.state?.courseId) {
      setPinnedCourse(location.state.courseId);
      navigate(location.pathname, { replace: true, state: null });
      return;
    }
    const id = location.state?.openStudentId;
    if (!id || loading) return;
    if (students.some(s => s.id === id)) setProfileId(id);
    // чистим state, иначе карточка снова откроется при возврате назад
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, loading, students]); // eslint-disable-line
  const toggleArchive = () => {
    setPinnedCourse(null);
    setSwiping(true);
    setArchive(a => !a);
    setSearch('');
    setTimeout(() => setSwiping(false), 520);
  };

  useEffect(() => { clearSelection(); }, [archive]);

  useLayoutEffect(() => {
    const measure = () => {
      const el = archive ? archivePaneRef.current : activePaneRef.current;
      if (el) setSwipeH(el.offsetHeight);
    };
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro) [activePaneRef.current, archivePaneRef.current].forEach(el => el && ro.observe(el));
    window.addEventListener('resize', measure);
    return () => { if (ro) ro.disconnect(); window.removeEventListener('resize', measure); };
  });

  const loadData = async () => {
    try {
      // Активные и отключённые тянем одним запросом — переключение «Ученики ⇄ Архив» без перезагрузки
      const [sRes, cRes] = await Promise.all([getStudents(true), getCourses()]);
      setStudents(sRes.data.students || []);
      setCourses(cRes.data || []);
    } catch { toast.error('Ошибка загрузки данных'); }
    finally { setLoading(false); }
  };

  // Код появился по требованию — обновляем строку без перезагрузки списка
  const patchCode = (id, code) =>
    setStudents(prev => prev.map(s => (s.id === id ? { ...s, progressCode: code } : s)));

  // Деактивация = мягкое удаление с платформы (ученик уезжает в архив)
  const handleDeactivate = async (student) => {
    if (!(await confirmDialog({ title: 'Перенести в архив?', message: `${student.name || student.email} уедет в архив и не сможет войти. Вернуть можно в любой момент.`, confirmText: 'В архив' }))) return;
    try {
      await setStudentActive(student.id, false);
      toast.success('Аккаунт отключён');
      loadData();
    } catch { toast.error('Ошибка'); }
  };

  const handleActivate = async (student) => {
    try {
      await setStudentActive(student.id, true);
      toast.success('Аккаунт активирован');
      loadData();
    } catch { toast.error('Ошибка'); }
  };

  // Полное удаление: аккаунт и все данные ученика стираются, в архив он не попадает
  const handleDeleteForever = async (student) => {
    if (!(await confirmDialog({
      title: 'Удалить ученика навсегда?',
      message: `${student.name || student.email} будет удалён полностью: аккаунт, курсы, прогресс, ответы и история. Восстановить нельзя — вернуться он сможет, только зарегистрировавшись заново.`,
      confirmText: 'Удалить навсегда',
    }))) return;
    try {
      await deleteStudentForever(student.id);
      toast.success('Ученик удалён');
      loadData();
    } catch (err) { toast.error(err?.response?.data?.message || 'Не удалось удалить ученика'); }
  };

  const handleInvite = async () => {
    if (!inviteFirst.trim()) return toast.error('Введите имя ученика');
    if (!inviteLast.trim()) return toast.error('Введите фамилию ученика');
    if (!inviteEmail.trim()) return toast.error('Введите email');
    if (!inviteCourseId) return toast.error('Выберите курс');
    const fullName = `${inviteFirst.trim()} ${inviteLast.trim()}`;
    setSaving(true);
    try {
      await inviteStudent({ name: fullName, email: inviteEmail, courseId: inviteCourseId, isTrial: inviteTrial });
      toast.success(`Ученик ${fullName} добавлен${inviteTrial ? ' как пробный' : ''}`);
      setInviteOpen(false); setInviteFirst(''); setInviteLast(''); setInviteEmail(''); setInviteCourseId(''); setInviteTrial(false);
      loadData();
    } catch (e) { toast.error(e.response?.data?.message || 'Ошибка'); }
    finally { setSaving(false); }
  };

  const handleAssign = async () => {
    if (!selectedCourse) return toast.error('Выберите курс');
    setSaving(true);
    try {
      if (bulkAssign) {
        await Promise.all([...selectedIds].map(id => assignCourseToStudent(id, selectedCourse).catch(() => null)));
        toast.success('Курс назначен выбранным');
        clearSelection();
      } else {
        await assignCourseToStudent(selectedStudent.id, selectedCourse);
        toast.success('Курс назначен');
      }
      setAssignOpen(false); setSelectedCourse(''); setBulkAssign(false);
      loadData();
    } catch (e) { toast.error(e.response?.data?.message || 'Ошибка'); }
    finally { setSaving(false); }
  };

  const handleRemove = async (student, courseId, courseLabel) => {
    if (!(await confirmDialog({ title: 'Убрать с курса?', message: `${student.name || student.email} будет отчислён с курса «${courseLabel}».`, confirmText: 'Убрать' }))) return;
    try {
      await removeStudent(student.id, courseId);
      toast.success('Ученик убран с курса');
      loadData();
    } catch { toast.error('Ошибка при удалении'); }
  };

  const openZoomEdit = (student) => {
    setZoomEditStudent(student);
    setZoomEditValue(student.zoomLink || '');
    setZoomManual(false);
    setZoomEditOpen(true);
  };

  const handleZoomSave = async () => {
    if (!zoomEditStudent) return;
    setSaving(true);
    try {
      await setStudentZoomLink(zoomEditStudent.id, zoomEditValue.trim() || null);
      toast.success(zoomEditValue.trim() ? 'Zoom-ссылка сохранена' : 'Zoom-ссылка удалена');
      setZoomEditOpen(false);
      setZoomEditStudent(null);
      setZoomEditValue('');
      setZoomManual(false);
      loadData();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader />;

  /* ── Активные / архив ── */
  const inactiveCount = students.filter(s => s.isActive === false).length;

  /* Состав групп выбранного курса. Считаем по тем же ученикам, что видны в
     разделе (активные или архив), и до фильтра по группе — иначе выбранная
     плашка показывала бы своё число, а соседняя ноль.
     Без useMemo: этот код идёт после раннего return по загрузке, а хук там
     ломает порядок хуков между рендерами. Список короткий, считать дёшево. */
  const groupCounts = (() => {
    if (!pickedCourse) return null;
    const onCourse = students.filter(s =>
      (archive ? s.isActive === false : s.isActive !== false)
      && s.StudentCourses?.some(sc => sc.courseId === pickedCourse || sc.Course?.id === pickedCourse));
    return {
      main: onCourse.filter(s => !s.isTrial).length,
      trial: onCourse.filter(s => s.isTrial).length,
    };
  })();

  /* ── Filter ── */
  const matches = (s) => {
    const name = (s.name || '').toLowerCase();
    const email = (s.email || '').toLowerCase();
    const code = (s.progressCode || '').toLowerCase();
    const q = search.trim().toLowerCase();
    const matchSearch = !q || name.includes(q) || email.includes(q) || code.includes(q);
    const hasCourse = s.StudentCourses?.length > 0;
    const matchFilter =
      filterCourse === 'all' ? true :
      filterCourse === 'none' ? !hasCourse :
      s.StudentCourses?.some(sc => sc.Course?.examType === filterCourse);
    const matchPinned = pinnedCourse == null
      || s.StudentCourses?.some(sc => sc.courseId === pinnedCourse || sc.Course?.id === pinnedCourse);
    const matchPicked = pickedCourse == null
      || s.StudentCourses?.some(sc => sc.courseId === pickedCourse || sc.Course?.id === pickedCourse);
    const matchGroup = pickedGroup == null
      || (pickedGroup === 'trial' ? !!s.isTrial : !s.isTrial);
    return matchSearch && matchFilter && matchPinned && matchPicked && matchGroup;
  };

  /* ── Sort ── */
  const courseKey = (s) => s.StudentCourses?.map(sc => sc.Course?.name).filter(Boolean).join(', ') || '';
  const sortList = (list) => [...list].sort((a, b) => {
    let cmp;
    if (sortBy === 'name') {
      cmp = (a.name || 'яяя').localeCompare(b.name || 'яяя', 'ru');
    } else if (sortBy === 'course') {
      const ca = courseKey(a), cb = courseKey(b);
      // «Без курса» — всегда в конце, независимо от направления
      if (!ca && cb) return 1;
      if (ca && !cb) return -1;
      cmp = ca.localeCompare(cb, 'ru');
    } else if (sortBy === 'active') {
      cmp = new Date(a.lastActiveAt || 0) - new Date(b.lastActiveAt || 0);
    } else if (sortBy === 'code') {
      // Сначала пробные, затем те, у кого код уже выдан, потом остальные
      const key = (x) => (x.isTrial ? 2 : 0) + (x.progressCode ? 1 : 0);
      cmp = key(a) - key(b);
    } else if (sortBy === 'done') {
      cmp = (a.progressStats?.progress || 0) - (b.progressStats?.progress || 0);
    } else {
      cmp = new Date(a.created_at || 0) - new Date(b.created_at || 0);
    }
    // при равенстве — стабильно по дате (новые сверху)
    if (cmp === 0) return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    return sortDir === 'asc' ? cmp : -cmp;
  });

  // Обе полки считаем всегда — они лежат рядом в одном «окне» и переезжают сдвигом
  const activeList = sortList(students.filter(s => s.isActive !== false && matches(s)));
  const archiveList = sortList(students.filter(s => s.isActive === false && matches(s)));
  const sorted = archive ? archiveList : activeList;

  const toggleSort = (col) => {
    setPinnedCourse(null);
    if (sortBy === col) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(col); setSortDir(col === 'name' || col === 'course' ? 'asc' : 'desc'); }
  };

  const profileStudent = students.find(s => s.id === profileId) || null;
  const allSelected = sorted.length > 0 && sorted.every(s => selectedIds.has(s.id));

  /* ── Карточка ученика в режиме «плитка» ── */
  const renderCard = (student) => {
    const stats = student.progressStats || {};
    const webPct = stats.webTotal ? Math.round(((stats.webDone || 0) / stats.webTotal) * 100) : 0;
    // ДЗ считаем как «Пройдено» в списке — по заданиям курса, а не по номерам внутри них
    const hwPct = stats.hwTotal ? Math.round(((stats.hwDone || 0) / stats.hwTotal) * 100) : 0;

    return (
      <div key={student.id} className="stu-card" onClick={() => setProfileId(student.id)} title="Открыть карточку ученика"
        style={{ borderRadius: 18, padding: 18, cursor: 'pointer', background: 'var(--card)', border: '1px solid var(--border)', transition: 'box-shadow .15s' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 13 }}>
          <span onClick={(e) => { e.stopPropagation(); toggleSelect(student.id); }} style={{ display: 'inline-flex', alignItems: 'center', paddingTop: 14, cursor: 'pointer' }} title="Выбрать">
            <Check on={selectedIds.has(student.id)} />
          </span>
          <div style={{ width: 46, height: 46, borderRadius: 15, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', background: avatarColor(student.id) }}>
            {student.avatar
              ? <img src={student.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <AvatarIcon id={student.id} size={24} />}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {student.name || 'Без имени'}
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{student.email}</div>
          </div>
          <CodeChip student={student} onCode={patchCode} size="card" />
        </div>

        <div style={{ margin: '15px 0 14px', padding: '13px 0', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)', display: 'flex', gap: 12 }}>
          <Bar label="Вебинары" value={`${stats.webDone || 0}/${stats.webTotal || 0}`} pct={webPct} />
          <Bar label="ДЗ" value={`${stats.hwDone || 0}/${stats.hwTotal || 0}`} pct={hwPct} />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <CourseCell student={student} compact />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <OnlineCell student={student} size={12.5} />
            <DoneCell student={student} />
          </div>
        </div>
      </div>
    );
  };

  /* ── Строка ученика ── */
  const renderRow = (student) => {
    return (
      <div key={student.id} className="stu-row" onClick={() => setProfileId(student.id)} title="Открыть карточку ученика"
        style={{ background: 'var(--card)', border: '1px solid var(--border)', cursor: 'pointer', transition: 'border-color .15s, box-shadow .15s' }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(124,77,255,0.4)'; e.currentTarget.style.boxShadow = '0 12px 30px -18px rgba(124,77,255,0.55)'; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}>

        {/* чекбокс выбора */}
        <span className="stu-c-check" onClick={(e) => { e.stopPropagation(); toggleSelect(student.id); }} style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }} title="Выбрать">
          <Check on={selectedIds.has(student.id)} />
        </span>

        {/* ученик */}
        <div className="stu-c-name" style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
          <div style={{ width: 50, height: 50, borderRadius: 16, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', background: avatarColor(student.id), boxShadow: `0 6px 14px -6px ${avatarColor(student.id)}` }}>
            {student.avatar
              ? <img src={student.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <AvatarIcon id={student.id} size={26} />}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--text-1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {student.name || 'Без имени'}
              {student.isActive === false && (
                <span style={{ marginLeft: 8, background: 'rgba(229,72,77,0.13)', color: '#e5484d', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20 }}>Отключён</span>
              )}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{student.email}</div>
          </div>
        </div>

        {/* код */}
        <div className="stu-c-code" style={{ minWidth: 0 }}><CodeChip student={student} onCode={patchCode} /></div>

        {/* курс */}
        <div className="stu-c-course" style={{ minWidth: 0 }}><CourseCell student={student} onRemove={(cid, nm) => handleRemove(student, cid, nm)} /></div>

        {/* пройдено */}
        <div className="stu-c-done"><DoneCell student={student} /></div>

        {/* был(а) онлайн */}
        <div className="stu-c-online" style={{ minWidth: 0 }}><OnlineCell student={student} /></div>

        <span className="stu-chev stu-c-chev" style={{ display: 'inline-flex' }}><IcChevron /></span>
      </div>
    );
  };

  /* Одна полка списка — используется и для активных, и для архива */
  const renderShelf = (list, isArchive) => {
    if (list.length === 0) {
      return (
        <div style={{ textAlign: 'center', padding: '46px 0', color: 'var(--text-3)', fontSize: 14, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 16 }}>
          {search ? 'Ничего не найдено' : (isArchive ? 'В архиве пусто' : 'Учеников пока нет')}
        </div>
      );
    }
    if (view === 'cards') {
      return (
        <div className="stu-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(310px, 1fr))', gap: 14 }}>
          {list.map(renderCard)}
        </div>
      );
    }
    return (
      <div className="stu-scroll">
        <div className="stu-inner" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div className="stu-head">
            <span className="stu-c-check" onClick={() => setSelectedIds(allSelected ? new Set() : new Set(list.map(s => s.id)))} style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }} title="Выбрать всех">
              <Check on={allSelected} />
            </span>
            <span className="stu-c-name"><SortHead label="Ученик" col="name" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} /></span>
            <span className="stu-c-code"><SortHead label="Код" col="code" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} /></span>
            <span className="stu-c-course"><SortHead label="Курс" col="course" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} /></span>
            <span className="stu-c-done"><SortHead label="Пройдено" col="done" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} /></span>
            <span className="stu-c-online"><SortHead label="Был(а) онлайн" col="active" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} /></span>
            <span className="stu-c-chev" />
          </div>
          {list.map(renderRow)}
        </div>
      </div>
    );
  };

  return (
    <>
      {/* ── Header row ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 className="s-page-title">{archive ? 'Архив' : 'Ученики'}</h1>
          <p className="s-page-sub">{archive ? 'Отключённые аккаунты — доступ можно вернуть' : 'Управление учениками платформы'}</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* Архив — только тем, кто может туда отправлять: куратор его не видит */}
          {can('students.archive') && (
            <button
              className={`dash-drafts-btn stu-arch-btn${archive ? ' on' : ''}`}
              onClick={toggleArchive}
              title={archive ? 'Вернуться к активным ученикам' : `Архив${inactiveCount > 0 ? ` · ${inactiveCount} отключённых` : ' пуст'}`}
              aria-label={archive ? 'Вернуться к активным ученикам' : 'Архив'}>
              {archive ? <IcBack /> : <IcArchive />}
            </button>
          )}
          {can('students.invite') && (
            <button className="action-btn purple" onClick={() => setInviteOpen(true)}>
              + Добавить ученика
            </button>
          )}
        </div>
      </div>

      {/* ── Search + filter + вид ── */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)' }}>
            <SearchIcon />
          </span>
          <input
            value={search} onChange={e => { setPinnedCourse(null); setSearch(e.target.value); }}
            placeholder="Имя, email или код ученика..."
            style={{ width: '100%', padding: '10px 14px 10px 38px', border: '1px solid var(--border)', borderRadius: 10, fontSize: 14, color: 'var(--text-1)', outline: 'none', boxSizing: 'border-box', background: 'var(--card)' }}
          />
        </div>
        <div className="stu-filters">
          {/* Ширина кнопки постоянная: выбранное название обрезается многоточием,
              а не раздвигает соседей */}
          <button type="button" className="stu-pick" onClick={() => setCoursePick(true)}
            title={pickedCourse ? (courses.find(c => c.id === pickedCourse)?.name || '') : undefined}>
            {/* Иконка выбранного курса — она же на его обложке */}
            {pickedCourse && courses.find(c => c.id === pickedCourse)?.icon
              ? <LibIcon icon={courses.find(c => c.id === pickedCourse).icon} size={16} />
              : <IcCourses />}
            <span className="stu-pick-t">
              {pickedCourse
                ? (courses.find(c => c.id === pickedCourse)?.name || 'Курс')
                : filterCourse === 'none' ? 'Без курса' : 'Выбрать курс'}
            </span>
            {(pickedCourse || filterCourse === 'none') && (
              <span className="stu-pick-x" title="Показать всех"
                onClick={(e) => { e.stopPropagation(); setPickedCourse(null); setPickedGroup(null); setFilterCourse('all'); }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </span>
            )}
          </button>
        </div>
        {/* переключатель раскладки: строки / плитка */}
        <div className="stu-view">
          <button type="button" className={`stu-view-btn${view === 'rows' ? ' on' : ''}`} onClick={() => setViewMode('rows')} title="Строками" aria-label="Строками"><IcRows /></button>
          <button type="button" className={`stu-view-btn${view === 'cards' ? ' on' : ''}`} onClick={() => setViewMode('cards')} title="Плиткой" aria-label="Плиткой"><IcCards /></button>
        </div>
      </div>

      {/* ── Группы курса ──────────────────────────────────────────────────
          Появляются, только когда выбран конкретный курс: без него «основная»
          и «пробная» ничего не значат — у ученика может быть два курса сразу. */}
      {pickedCourse && groupCounts && (
        <div className="stu-groups">
          {[
            { key: 'main', title: 'Основная группа', sub: 'полный доступ к курсу', n: groupCounts.main, Ic: IcGroupMain },
            { key: 'trial', title: 'Пробная группа', sub: 'ближайшие занятия курса', n: groupCounts.trial, Ic: IcGroupMain },
          ].map(g => (
            <button key={g.key} type="button"
              className={`stu-grp${pickedGroup === g.key ? ' on' : ''} g-${g.key}`}
              onClick={() => setPickedGroup(pickedGroup === g.key ? null : g.key)}
              title={pickedGroup === g.key ? 'Показать обе группы' : `Показать только: ${g.title}`}>
              <span className="stu-grp-ic"><g.Ic /></span>
              <span className="stu-grp-main">
                <span className="stu-grp-t">{g.title}</span>
                <span className="stu-grp-s">{g.sub}</span>
              </span>
              <span className="stu-grp-cell">
                <span className="stu-grp-num">{g.n}</span>
                <span className="stu-grp-cap">{plural(g.n, ['ученик', 'ученика', 'учеников'])}</span>
              </span>
            </button>
          ))}
        </div>
      )}

      {/* ── Массовые действия ── */}
      <div className="stu-bulk-wrap" data-open={selectedIds.size > 0}>
        <div className="stu-bulk-inner">
          <div className="stu-bulk">
            <span className="stu-bulk-lab">Выбрано: {selectedIds.size}</span>
            <div style={{ flex: 1 }} />
            {can('students.enroll') && (
              <button className="action-btn purple" style={{ padding: '8px 14px', fontSize: 13 }} onClick={() => { setBulkAssign(true); setSelectedCourse(''); setAssignOpen(true); }}>Добавить курс</button>
            )}
            {/* Перенос в группу — там же, где и смена пометки «пробник» в карточке */}
            {!archive && can('students.edit') && (
              <>
                <button className="action-btn" style={{ padding: '8px 14px', fontSize: 13 }} onClick={() => bulkGroup(false)}>В основную</button>
                <button className="action-btn" style={{ padding: '8px 14px', fontSize: 13 }} onClick={() => bulkGroup(true)}>В пробную</button>
              </>
            )}
            {!archive
              ? can('students.archive') && <button className="action-btn danger" style={{ padding: '8px 14px', fontSize: 13 }} onClick={bulkDeactivate}>В архив</button>
              : can('students.delete') && <button className="action-btn danger" style={{ padding: '8px 14px', fontSize: 13 }} onClick={bulkDeleteForever}>Удалить навсегда</button>}
          </div>
        </div>
      </div>

      {/* ── Список: активные ⇄ архив в одном «окне», переезд плавным сдвигом ── */}
      <div className={`stu-swipe-win${swiping ? ' anim' : ''}`} style={{ height: swipeH ? `${swipeH}px` : undefined }}>
        <div className={`stu-swipe${swiping ? ' anim' : ''}`} style={{ transform: archive ? 'translateX(-50%)' : 'none' }}>
          <div className="stu-swipe-pane" ref={activePaneRef}>{renderShelf(activeList, false)}</div>
          <div className="stu-swipe-pane" ref={archivePaneRef}>{renderShelf(archiveList, true)}</div>
        </div>
      </div>

      {/* ── Карточка ученика ── */}
      {/* Выбор курса: те же карточки, что на «Статистике», только мини */}
      <Modal isOpen={coursePick} onClose={() => setCoursePick(false)} bare>
        <div className="adm-modal stu-cm" onClick={e => e.stopPropagation()}>
          <div className="stu-cm-head">
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="stu-cm-t">Выбрать курс</div>
              <div className="stu-cm-s">покажем только учеников этого курса</div>
            </div>
            <button type="button" className="adm-x" onClick={() => setCoursePick(false)} aria-label="Закрыть">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
            </button>
          </div>
          <div className="stu-cm-grid">
            {/* «Все ученики» — сбрасывает фильтр по курсу */}
            <button type="button" className={`stu-cc plain${!pickedCourse && filterCourse !== 'none' && !pinnedCourse ? ' on' : ''}`}
              onClick={() => { setPickedCourse(null); setPickedGroup(null); setFilterCourse('all'); setPinnedCourse(null); setCoursePick(false); }}>
              <span className="stu-cc-plain">
                <span className="stu-cc-plain-ic"><IcAllStudents /></span>
                <span className="stu-cc-plain-t">Все ученики</span>
              </span>
              <span className="stu-cc-foot">
                {(() => { const n = students.filter(st => st.isActive !== false).length; return `${n} ${plural(n, ['ученик', 'ученика', 'учеников'])}`; })()}
              </span>
            </button>
            {/* Все курсы, включая черновики: ученики бывают и на неопубликованных */}
            {/* Сначала опубликованные курсы, потом черновики; «Без курса» — в самом конце */}
            {[...courses].sort((a, b) => (a.isActive === false) - (b.isActive === false)).map(c => {
              const cnt = students.filter(st => st.isActive !== false
                && st.StudentCourses?.some(sc => sc.courseId === c.id || sc.Course?.id === c.id)).length;
              const col = c.color || '#7c4dff';
              return (
                <button key={c.id} type="button"
                  className={`stu-cc${pickedCourse === c.id ? ' on' : ''}`}
                  onClick={() => { setPickedCourse(c.id); setPickedGroup(null); setFilterCourse('all'); setPinnedCourse(null); setCoursePick(false); }}>
                  <span className="stu-cc-cover" style={{
                    '--st-t-light': `${col}80`, '--st-t-dark': `${col}38`,
                    '--st-chip-fg': shade(col, -42), '--st-chip-fg-dark': shade(col, 55), '--st-c': col,
                  }}>
                    <span className="st-crs-pat"><CoverPattern id={`cm${c.id}`} /></span>
                    <span className="stu-cc-top">
                      {c.examType && <span className="st-crs-lvl"><i />{c.examType}</span>}
                      <span className="st-crs-ic"><LibIcon icon={c.icon} size={15} /></span>
                    </span>
                    <span className="stu-cc-name">{c.name}</span>
                  </span>
                  <span className="stu-cc-foot">
                    {cnt} {plural(cnt, ['ученик', 'ученика', 'учеников'])}
                    {c.isActive === false && <i className="stu-cc-draft">черновик</i>}
                  </span>
                </button>
              );
            })}
            <button type="button" className={`stu-cc plain${filterCourse === 'none' ? ' on' : ''}`}
              onClick={() => { setPickedCourse(null); setPickedGroup(null); setFilterCourse('none'); setPinnedCourse(null); setCoursePick(false); }}>
              <span className="stu-cc-plain">
                <span className="stu-cc-plain-t">Без курса</span>
              </span>
              <span className="stu-cc-foot">
                {(() => { const n = students.filter(st => st.isActive !== false && !(st.StudentCourses?.length > 0)).length; return `${n} ${plural(n, ['ученик', 'ученика', 'учеников'])}`; })()}
              </span>
            </button>
          </div>
        </div>
      </Modal>

      <StudentProfileModal
        student={profileStudent}
        isOpen={!!profileStudent}
        onClose={() => setProfileId(null)}
        /* Кнопки в карточке зависят от роли: чего нельзя — того и не показываем.
           Куратор видит карточку целиком, но только смотрит. */
        onAddCourse={can('students.enroll') ? (s) => { setSelectedStudent(s); setSelectedCourse(''); setBulkAssign(false); setAssignOpen(true); } : null}
        onRemoveCourse={can('students.unenroll') ? (s, cid, nm) => handleRemove(s, cid, nm) : null}
        onEditZoom={openZoomEdit}
        canCode={can('students.code')}
        onUpdated={loadData}
        onDeactivate={can('students.archive') ? handleDeactivate : null}
        onActivate={can('students.archive') ? handleActivate : null}
        onDelete={can('students.delete') ? handleDeleteForever : null}
        readOnly={!can('students.edit')}
      />

      {/* ── Assign course modal ── */}
      <Modal isOpen={assignOpen} onClose={() => { setAssignOpen(false); setBulkAssign(false); }} title={bulkAssign ? `Добавить курс — выбрано ${selectedIds.size}` : `Добавить курс — ${selectedStudent?.name || selectedStudent?.email}`}>
        <div className="form-group">
          <label className="form-label">Курс</label>
          {(() => {
            const enrolledIds = new Set((bulkAssign ? [] : (selectedStudent?.StudentCourses || [])).map(sc => sc.Course?.id));
            const available = courses.filter(c => !enrolledIds.has(c.id));
            return available.length === 0 ? (
              <p style={{ fontSize: 13, color: 'var(--text-3)', margin: '4px 0' }}>Ученик уже записан на все курсы.</p>
            ) : (
              <Dropdown
                value={selectedCourse} onChange={setSelectedCourse} placeholder="Выберите курс"
                options={available.map(c => ({ value: String(c.id), label: `${c.name}${c.examType ? ` · ${c.examType}` : ''}` }))}
              />
            );
          })()}
        </div>
        <div className="form-actions">
          <button className="action-btn secondary" onClick={() => setAssignOpen(false)}>Отмена</button>
          <button className="action-btn purple" onClick={handleAssign} disabled={saving}>
            {saving ? 'Сохранение...' : 'Назначить'}
          </button>
        </div>
      </Modal>

      {/* ── Zoom edit modal ── */}
      {(() => {
        const auto = (zoomEditStudent?.StudentCourses || []).filter(sc => sc.zoomJoinUrl);
        const hasAuto = auto.length > 0;
        // Пока ссылку выдал Zoom, вписывать свою незачем — поле прячем за ссылкой
        const showManual = !hasAuto || zoomManual || !!zoomEditStudent?.zoomLink;
        const close = () => { setZoomEditOpen(false); setZoomEditStudent(null); setZoomEditValue(''); setZoomManual(false); };
        const copy = async (url) => {
          try { await navigator.clipboard.writeText(url); toast.success('Ссылка скопирована'); }
          catch { toast.error('Буфер обмена недоступен'); }
        };
        return (
          <Modal
            isOpen={zoomEditOpen}
            onClose={close}
            title={`Ссылка на вебинары — ${zoomEditStudent?.name || zoomEditStudent?.email || ''}`}
          >
            <div className="form-group">
              <label className="form-label">Персональная Zoom-ссылка</label>

              {hasAuto ? (
                <>
                  <div className="zoom-links">
                    {auto.map(sc => (
                      <div key={sc.id} className="zoom-link-row">
                        <div className="zoom-link-main">
                          <div className="zoom-link-course">{sc.Course?.name || 'Курс'}</div>
                          <div className="zoom-link-url">{sc.zoomJoinUrl}</div>
                        </div>
                        <button type="button" className="zoom-link-copy" title="Скопировать ссылку"
                          onClick={() => copy(sc.zoomJoinUrl)}>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                  <p style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 6 }}>
                    Ссылку выдал Zoom, когда ученика зачислили на курс — по ней он и входит с кнопки «Войти в урок». Менять её не нужно; у каждого курса своя.
                  </p>
                </>
              ) : (
                <>
                  <input
                    className="form-input"
                    type="url"
                    value={zoomEditValue}
                    onChange={e => setZoomEditValue(e.target.value)}
                    autoFocus
                  />
                  <p style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 6 }}>
                    Zoom пока не выдал ссылку по курсу — можно вписать её вручную. Оставьте пустым, чтобы удалить.
                  </p>
                </>
              )}

              {/* Запасное поле: нужно, только если Zoom ссылку не выдал */}
              {hasAuto && !showManual && (
                <button type="button" className="zoom-manual-toggle" onClick={() => setZoomManual(true)}>
                  Вписать запасную ссылку вручную
                </button>
              )}
              {hasAuto && showManual && can('zoom.manage') && (
                <div style={{ marginTop: 12 }}>
                  <label className="form-label">Запасная ссылка (вручную)</label>
                  <input
                    className="form-input"
                    type="url"
                    value={zoomEditValue}
                    onChange={e => setZoomEditValue(e.target.value)}
                  />
                  <p style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 6 }}>
                    Сработает только там, где у курса нет ссылки от Zoom. Оставьте пустым, чтобы удалить.
                  </p>
                </div>
              )}
            </div>

            <div className="form-actions">
              <button className="action-btn secondary" onClick={close}>
                {showManual ? 'Отмена' : 'Закрыть'}
              </button>
              {showManual && can('zoom.manage') && (
                <button className="action-btn purple" onClick={handleZoomSave} disabled={saving}>
                  {saving ? 'Сохранение...' : 'Сохранить'}
                </button>
              )}
            </div>
          </Modal>
        );
      })()}

      {/* ── Invite modal ── */}
      <Modal isOpen={inviteOpen} onClose={() => setInviteOpen(false)} title="Добавить ученика">
        <div className="fl-grid2">
          <div className="form-group">
            <label className="form-label">Имя</label>
            <input className="form-input" type="text" value={inviteFirst} onChange={e => setInviteFirst(e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Фамилия</label>
            <input className="form-input" type="text" value={inviteLast} onChange={e => setInviteLast(e.target.value)} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Email</label>
          <input className="form-input" type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">Курс</label>
          <Dropdown
            value={inviteCourseId} onChange={setInviteCourseId} placeholder="Выберите курс"
            options={[...courses].sort((a, b) => (a.isActive === false) - (b.isActive === false))
              .map(c => ({ value: String(c.id), label: c.name }))}
          />
        </div>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 9, padding: '10px 12px', borderRadius: 10,
          border: '1px solid var(--border)', background: inviteTrial ? 'var(--primary-light)' : 'var(--bg)',
          cursor: 'pointer', marginBottom: 10 }}>
          <input type="checkbox" checked={inviteTrial} onChange={e => setInviteTrial(e.target.checked)}
            style={{ marginTop: 2, accentColor: 'var(--primary)', width: 16, height: 16, flexShrink: 0 }} />
          <span>
            <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-1)' }}>Пробный</span>
            <span style={{ display: 'block', fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>
              Вместо даты доступа будет пометка «Пробный», и в «Доступах» такой ученик встаёт первым.
              Пометка снимется сама, когда вы продлите ему доступ.
            </span>
          </span>
        </label>

        <p style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 4 }}>
          Если пользователь уже зарегистрирован — будет добавлен к курсу. Иначе получит приглашение на почту.
        </p>
        <div className="form-actions">
          <button className="action-btn secondary" onClick={() => setInviteOpen(false)}>Отмена</button>
          <button className="action-btn purple" onClick={handleInvite} disabled={saving}>
            {saving ? 'Добавление...' : 'Добавить ученика'}
          </button>
        </div>
      </Modal>
    </>
  );
}
